import React, { useState } from 'react';
import {
  UnorderedListOutlined,
  ClockCircleOutlined,
  EditOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { Card, Button, Form, Input, Col, Row, Divider, message, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { isFunction, isNil } from 'lodash';
import moment from 'moment';
import { Link, useParams } from 'react-router-dom';

import { registerEventResult, clearMatchResult } from 'api/events';
import { downloadMatchReport } from 'utils/download';
import AvatarStatus from 'componentsv2/AvatarStatus/AvatarStatus';
import { EncounterModal } from 'componentsv2/util-components/EncounterModal/EncounterModal';
import { general, matchStatuses } from 'configs';
import { paths } from 'configs/paths';
import { messages } from 'constants/index';

const { Text } = Typography;
const { CANCELLED } = matchStatuses;
const { displayDate, displayTime12 } = general;
const { event } = paths;

const JornadasList = ({
  onLoadRounds,
  onLoadSets,
  match,
  isEdit = false,
  matchDayId,
  displayOnly = false,
}) => {
  const {
    id: matchId,
    name,
    date_time,
    event_result,
    home,
    visit,
    place_name,
    status: matchStatus,
  } = match || {};
  const [form] = Form.useForm();
  const breakPoint = useBreakpoint();
  let { eventId, groupId, matchDayId: matchDayParamId } = useParams();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditEncounter, setIsEditEncounter] = useState(false);
  const [eventResult, setEventResult] = useState(event_result);

  const showModal = isEdit => {
    setIsEditEncounter(isEdit);
    setIsModalVisible(true);
  };

  const onGlobalScoreEdit = async values => {
    const result = {
      club_score: Number(values.club_score),
      oponent_score: Number(values.opponent_score),
    };
    try {
      await registerEventResult(matchDayParamId, match.id, result);
      message.success('!Información actualizada!');
      setEventResult({
        home_score: result.club_score,
        visit_score: result.oponent_score,
      });
    } catch (_) {
      message.error(messages.onError);
    }
  };

  const onClearMatchResult = async () => {
    try {
      await clearMatchResult(matchId);
      isFunction(onLoadRounds) && (await onLoadRounds());
      isFunction(onLoadSets) && (await onLoadSets());
      setEventResult(null);
      form.setFieldsValue({ club_score: null, opponent_score: null });
      message.success(messages.onClearMatchResult);
    } catch (_) {
      message.error(messages.onError);
    }
  };

  const clearMatchResultLink = eventResult &&
    !isNil(eventResult?.home_score) &&
    !isNil(eventResult?.visit_score) && (
      <Row justify="center" style={{ marginTop: '1.5rem' }}>
        <Text className="cursor-pointer" onClick={onClearMatchResult} type="danger">
          Limpiar marcador
        </Text>
      </Row>
    );

  return (
    <>
      <EncounterModal
        onLoadRounds={onLoadRounds}
        match={match}
        matchDayId={matchDayId}
        isVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        isEditEncounter={isEditEncounter}
      />
      <Card
        style={{
          marginTop: 16,
          borderBottom: displayOnly && '5px solid #edf2f9',
        }}
        headStyle={{
          backgroundImage:
            'background-image: linear-gradient(to right, #ffffff, #f7f7f7, #eeeeee, #e6e6e6, #dedede)',
        }}
        type="inner"
        title={name}
      >
        <Col flex xs={24} sm={24} md={24} lg={24} xl={24}>
          <Row
            style={{ flexDirection: !useBreakpoint().md && 'column' }}
            justify="center"
            align="middle"
          >
            <AvatarStatus
              style={{
                width: useBreakpoint().md && '40%',
                justifyContent: 'center',
                marginBottom: !useBreakpoint().md && '20px',
                flexDirection: !useBreakpoint().sm && 'column',
                textAlign: !useBreakpoint().sm && 'center',
              }}
              size={60}
              src={home?.avatar_url}
              name={home?.name}
            />
            {isEdit ? (
              <Form
                form={form}
                id={`matchForm${matchId}`}
                layout="inline"
                onFinish={values => onGlobalScoreEdit(values)}
                size="large"
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: !breakPoint.md ? '100%' : '20%',
                }}
              >
                <Form.Item
                  initialValue={eventResult && eventResult?.home_score}
                  style={{
                    marginLeft: '0px',
                    width: '40px',
                    marginBottom: '0px',
                  }}
                  name="club_score"
                  rules={[
                    {
                      required: true,
                      message: 'Requerido',
                    },
                  ]}
                >
                  <Input
                    style={{ padding: '0.65rem 0.25rem', textAlign: 'center' }}
                    placeholder={0}
                  />
                </Form.Item>
                -
                <Form.Item
                  initialValue={eventResult && eventResult?.visit_score}
                  style={{
                    marginRight: '0px',
                    marginLeft: '16px',
                    width: '40px',
                    marginBottom: '0px',
                  }}
                  name="opponent_score"
                  rules={[
                    {
                      required: true,
                      message: 'Requerido',
                    },
                  ]}
                >
                  <Input
                    style={{ padding: '0.65rem 0.25rem', textAlign: 'center' }}
                    placeholder={0}
                  />
                </Form.Item>
              </Form>
            ) : (
              <div
                style={{
                  fontSize: '35px',
                  justifyContent: 'center',
                  width: !breakPoint.sm ? '100%' : '20%',
                }}
                className="avatar-status-name flex"
              >
                {`${eventResult?.home_score ?? '0'} - ${eventResult?.visit_score ?? '0'}`}
              </div>
            )}
            <AvatarStatus
              style={{
                width: useBreakpoint().md && '40%',
                justifyContent: 'center',
                marginTop: !useBreakpoint().md && '20px',
                flexDirection: !useBreakpoint().sm && 'column',
                textAlign: !useBreakpoint().sm && 'center',
              }}
              size={60}
              src={visit?.avatar_url}
              inverted={true}
              name={visit?.name}
            />
          </Row>
        </Col>
        {!displayOnly && <Divider />}
        {isEdit ? (
          <Col flex xs={24} sm={24} md={24} lg={24} xl={24}>
            <Row justify="center">
              <Button
                form={`matchForm${matchId}`}
                htmlType="submit"
                type="primary"
                style={{ marginTop: '15px' }}
              >
                GUARDAR MARCADOR
              </Button>
            </Row>
            {clearMatchResultLink}
          </Col>
        ) : date_time && !displayOnly && matchStatus !== CANCELLED ? (
          <>
            <Col flex xs={24} sm={24} md={24} lg={24} xl={24}>
              <Row justify="space-around" align="middle">
                <p>
                  <b>Fecha:</b> {`${moment(date_time).format(displayDate)}`}
                </p>
                <p>
                  <b>Hora:</b> {`${moment(date_time).format(displayTime12)}`}
                </p>
                <p>
                  <b>Lugar:</b> {`${place_name}`}
                </p>
              </Row>
            </Col>
            <Divider></Divider>
            <Col flex xs={24} sm={24} md={24} lg={24} xl={24}>
              <Row justify="center">
                <Button
                  className={`btn--icon w-48 ${breakPoint.sm ? '' : 'mt-4'}`}
                  icon={<EditOutlined />}
                  onClick={() => showModal(true)}
                  type="primary"
                >
                  EDITAR ENCUENTRO
                </Button>
                <Link to={event.journeys(eventId, groupId, matchDayId, matchId)}>
                  <Button
                    className={`btn--icon w-48 ${breakPoint.sm ? 'ml-8' : 'mt-4'}`}
                    icon={<UnorderedListOutlined />}
                    type="primary"
                  >
                    VER MARCADOR
                  </Button>
                </Link>
                <Button
                  className={`btn--icon w-56 ${breakPoint.sm ? 'ml-8' : 'mt-4'}`}
                  icon={<FilePdfOutlined />}
                  onClick={() => downloadMatchReport(match)}
                  type="primary"
                >
                  DESCARGAR ALINEACIÓN
                </Button>
              </Row>
            </Col>
          </>
        ) : !displayOnly ? (
          <Col flex xs={24} sm={24} md={24} lg={24} xl={24}>
            <Row justify="center">
              <Button
                className="btn--icon"
                icon={<ClockCircleOutlined />}
                onClick={() => showModal(false)}
                type="primary"
              >
                PROGRAMAR ENCUENTRO
              </Button>
            </Row>
          </Col>
        ) : (
          clearMatchResultLink
        )}
      </Card>
    </>
  );
};

export default JornadasList;
